import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { ordersApi } from "@/services/api";
import type { Order } from "@/services/types/domain";
import type { FrontendPaginatedResponse, PaginationMeta } from "@/services/types/api";
import { formatPrice, tOrderStatus, tPaymentStatus, orderVariant, paymentVariant, refundVariant, tRefundStatus } from "@/utils/formatters";
import { toast } from "react-hot-toast";

// ————————————————————————————————————————————
// Reglas de negocio
// ————————————————————————————————————————————
const CANNOT_CANCEL_STATES = ["shipped", "delivered", "cancelled", "returned"] as const;
const canCancel = (o: Order) => !CANNOT_CANCEL_STATES.includes((o as any).status);
const isDelivered = (o: Order) => (o as any).status === "delivered";

// ————————————————————————————————————————————
// Diálogo simple reutilizable (confirm/cancel)
// ————————————————————————————————————————————
const ConfirmDialog: React.FC<{
    open: boolean;
    title: string;
    description?: string;
    confirmText?: string;
    cancelText?: string;
    loading?: boolean;
    onConfirm: () => void;
    onClose: () => void;
    children?: React.ReactNode;
}> = ({ open, title, description, confirmText = "Confirmar", cancelText = "Cerrar", loading, onConfirm, onClose, children }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="relative z-[61] w-full max-w-md rounded-2xl border bg-white p-5 shadow-xl">
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                {description && <p className="mt-1 text-sm text-gray-600">{description}</p>}
                {children && <div className="mt-4">{children}</div>}
                <div className="mt-5 flex items-center justify-end gap-2">
                    <Button variant="outline" onClick={onClose} disabled={loading}> {cancelText} </Button>
                    <Button onClick={onConfirm} disabled={loading}>{loading ? "Procesando…" : confirmText}</Button>
                </div>
            </div>
        </div>
    );
};

// ————————————————————————————————————————————
// Diálogo de solicitud de reembolso
// ————————————————————————————————————————————
type RefundDialogState = {
    open: boolean;
    orderId: number | null;
    loadingItems: boolean;
    items: Array<{ order_item_id: number; name: string; maxQty: number; qty: number }>;
    reason: string;
    busy: boolean;
};

const RefundRequestDialog: React.FC<{
    state: RefundDialogState;
    setState: React.Dispatch<React.SetStateAction<RefundDialogState>>;
    setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
}> = ({ state, setState, setOrders }) => {
    const totalQty = state.items.reduce((acc, it) => acc + (Number(it.qty) || 0), 0);
    const invalid = totalQty <= 0 || state.items.some(it => it.qty < 0 || it.qty > it.maxQty);

    const setQty = (order_item_id: number, qty: number) =>
        setState(s => ({ ...s, items: s.items.map(it => it.order_item_id === order_item_id ? { ...it, qty } : it) }));

    const selectAll = () =>
        setState(s => ({ ...s, items: s.items.map(it => ({ ...it, qty: it.maxQty })) }));

    const clearAll = () =>
        setState(s => ({ ...s, items: s.items.map(it => ({ ...it, qty: 0 })) }));

    const onConfirm = async () => {
        const reason = state.reason.trim();
        if (reason.trim().length < 10) {
            toast.error("Debes ingresar una razón con al menos 10 caracteres.");
            return
        }
        if (invalid || !state.orderId) return;
        try {
            setState(s => ({ ...s, busy: true }));
            const payload = {
                reason: state.reason.trim(),
                items: state.items.filter(it => it.qty > 0).map(it => ({ order_item_id: it.order_item_id, quantity: it.qty })),
            };
            await ordersApi.requestRefund(state.orderId, payload);
            setOrders((prev: any) => prev.map((o: any) => o.id === state.orderId ? { ...o, refund_request_status: "pending" } : o));
            toast.success("Solicitud de reembolso enviada");
            setState({ open: false, orderId: null, loadingItems: false, items: [], reason: "", busy: false });
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudo solicitar el reembolso");
            setState(s => ({ ...s, busy: false }));
        }
    };

    if (!state.open) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/30" onClick={() => setState(s => ({ ...s, open: false }))} />
            <div className="relative z-[61] w-full max-w-2xl rounded-2xl border bg-white p-5 shadow-xl">
                <h3 className="text-lg font-semibold text-gray-900">Solicitar reembolso</h3>
                <p className="mt-1 text-sm text-gray-600">
                    Indica el motivo y las cantidades a reembolsar por cada artículo recibido.
                </p>

                {state.loadingItems ? (
                    <div className="mt-6 text-sm text-gray-500">Cargando artículos…</div>
                ) : (
                    <>
                        <div className="mt-4 max-h-[45vh] overflow-auto rounded-xl border">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Artículo</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Comprado</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Reembolso</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 bg-white">
                                    {state.items.map((it) => (
                                        <tr key={it.order_item_id}>
                                            <td className="px-4 py-2 text-sm text-gray-800">{it.name}</td>
                                            <td className="px-4 py-2 text-right text-sm text-gray-700">{it.maxQty}</td>
                                            <td className="px-4 py-2 text-right">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={it.maxQty}
                                                    value={it.qty}
                                                    onChange={(e) => setQty(it.order_item_id, Math.max(0, Math.min(it.maxQty, Number(e.target.value) || 0)))}
                                                    className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm text-right"
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-3 flex items-center gap-2">
                            <Button variant="outline" onClick={selectAll}>Seleccionar todo</Button>
                            <Button variant="outline" onClick={clearAll}>Limpiar</Button>
                            <div className="ml-auto text-sm text-gray-600">Total a reembolsar: <strong>{totalQty}</strong> und.</div>
                        </div>

                        <div className="mt-4">
                            <label className="text-sm text-gray-700">Motivo (obligatorio)</label>
                            <textarea
                                rows={3}
                                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                placeholder="Ej. Producto defectuoso / No corresponde a lo publicado"
                                value={state.reason}
                                onChange={(e) => setState(s => ({ ...s, reason: e.target.value }))}
                            />
                        </div>
                    </>
                )}

                <div className="mt-5 flex items-center justify-end gap-2">
                    <Button variant="outline" onClick={() => setState(s => ({ ...s, open: false }))} disabled={state.busy}>Cerrar</Button>
                    <Button onClick={onConfirm} disabled={state.busy || state.loadingItems || invalid}>
                        {state.busy ? "Enviando…" : "Enviar solicitud"}
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ————————————————————————————————————————————
// Página de pedidos del comprador
// ————————————————————————————————————————————
const OrdersPage: React.FC = () => {
    const navigate = useNavigate();

    // Filtros/control
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [status, setStatus] = useState<string | undefined>(undefined);
    const [sort, setSort] = useState<string>("-created_at");

    // Resumen
    const [summary, setSummary] = useState<any>(null);

    // Lista y paginación
    const [orders, setOrders] = useState<Order[]>([]);
    const [meta, setMeta] = useState<PaginationMeta>({
        current_page: 1, from: 0, last_page: 1, per_page: 10, to: 0, total: 0,
    });
    //const [links, setLinks] = useState<PaginationLinks | null>(null);

    const [loadingSummary, setLoadingSummary] = useState(true);
    const [loadingList, setLoadingList] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Cancel dialog
    const [cancelDialog, setCancelDialog] = useState<{ open: boolean; orderId: number | null; reason: string; busy: boolean }>({
        open: false, orderId: null, reason: "", busy: false,
    });

    // Refund dialog
    const [refundDialog, setRefundDialog] = useState<RefundDialogState>({
        open: false, orderId: null, loadingItems: false, items: [], reason: "", busy: false,
    });

    // Cargar summary
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoadingSummary(true);
                const data = await ordersApi.getOrdersSummary();
                if (mounted) setSummary(data);
            } catch { }
            finally { if (mounted) setLoadingSummary(false); }
        })();
        return () => { mounted = false; };
    }, []);

    // Cargar lista
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoadingList(true);
                setError(null);
                const resp: FrontendPaginatedResponse<Order> = await ordersApi.getOrders({ page, per_page: perPage, status, sort });
                if (mounted) {
                    setOrders(resp.data);
                    setMeta(resp.meta);
                    //setLinks(resp.links);
                }
            } catch (err: any) {
                if (mounted) setError(err?.response?.data?.message || err?.message || "No se pudieron cargar los pedidos");
            } finally {
                if (mounted) setLoadingList(false);
            }
        })();
        return () => { mounted = false; };
    }, [page, perPage, status, sort]);

    /*const statusTabs: { key?: string; label: string }[] = [
        { key: undefined as unknown as string, label: "Todos" },
        { key: "pending", label: "Pendiente" },
        { key: "processing", label: "Procesando" },
        { key: "shipped", label: "Enviado" },
        { key: "delivered", label: "Entregado" },
        { key: "cancelled", label: "Cancelado" },
        { key: "returned", label: "Devuelto" },
    ];*/

    const showingFrom = useMemo(() => meta.from || 0, [meta]);
    const showingTo = useMemo(() => meta.to || 0, [meta]);

    // ——— Cancelar pedido ———
    const requestCancel = (orderId: number) => {
        const order = orders.find(o => o.id === orderId);
        if (!order) return;
        if (!canCancel(order)) {
            toast.error("No puedes cancelar un pedido enviado, entregado o previamente cancelado.");
            return;
        }
        setCancelDialog({ open: true, orderId, reason: "", busy: false });
    };

    const confirmCancel = async () => {
        const { orderId } = cancelDialog;

        if (!orderId) return;
        try {
            setCancelDialog(s => ({ ...s, busy: true }));
            await ordersApi.cancelOrder(orderId, cancelDialog.reason);
            // Actualiza localmente
            setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, status: "cancelled" as const } : o)));
            toast.success("Pedido cancelado");
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudo cancelar el pedido");
        } finally {
            setCancelDialog({ open: false, orderId: null, reason: "", busy: false });
        }
    };

    // ——— Solicitar reembolso ———
    const requestRefund = async (orderId: number) => {
        const order = orders.find(o => o.id === orderId);
        if (!order) return;
        if (!isDelivered(order)) {
            toast.error("Solo puedes solicitar reembolso de pedidos entregados.");
            return;
        }
        if (order.refund_request_status === "pending") {
            toast.error("Ya existe una solicitud de reembolso pendiente para este pedido.");
            return;
        }
        setRefundDialog({ open: true, orderId, loadingItems: true, items: [], reason: "", busy: false });
        try {
            // Cargar ítems del pedido
            const detail: any = await ordersApi.getOrderById(orderId);
            const items = Array.isArray(detail?.items) ? detail.items : [];
            const mapped = items.map((it: any) => ({
                order_item_id: it.id,
                name: it.product_name || it.name || `Ítem ${it.id}`,
                maxQty: Number(it.quantity) || 0,
                qty: 0,
            }));
            setRefundDialog(s => ({ ...s, loadingItems: false, items: mapped }));
        } catch (err: any) {
            setRefundDialog({ open: false, orderId: null, loadingItems: false, items: [], reason: "", busy: false });
            toast.error(err?.response?.data?.message || err?.message || "No se pudieron cargar los ítems del pedido");
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-8">
            <div className="mx-auto w-full max-w-7xl">
                {/* Header */}
                <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">Mis pedidos</h1>
                        <p className="text-gray-600">Revisa el estado de tus compras, cancela o solicita reembolsos.</p>
                    </div>
                </div>

                {/* Summary cards (igual que antes) */}
                <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
                    <div className="rounded-2xl border bg-white p-5 shadow-sm">
                        <div className="text-sm text-gray-600">Pedidos totales</div>
                        <div className="mt-1 text-2xl font-bold text-gray-900">{loadingSummary ? "—" : summary?.total_orders ?? 0}</div>
                    </div>
                    <div className="rounded-2xl border bg-white p-5 shadow-sm">
                        <div className="text-sm text-gray-600">Pendientes</div>
                        <div className="mt-1 text-2xl font-bold text-gray-900">{loadingSummary ? "—" : summary?.status_summary?.pending ?? 0}</div>
                    </div>
                    <div className="rounded-2xl border bg-white p-5 shadow-sm">
                        <div className="text-sm text-gray-600">En camino</div>
                        <div className="mt-1 text-2xl font-bold text-gray-900">{loadingSummary ? "—" : summary?.status_summary?.shipped ?? 0}</div>
                    </div>
                    <div className="rounded-2xl border bg-white p-5 shadow-sm">
                        <div className="text-sm text-gray-600">Completados</div>
                        <div className="mt-1 text-2xl font-bold text-gray-900">{loadingSummary ? "—" : summary?.completed_orders ?? 0}</div>
                    </div>
                    <div className="rounded-2xl border bg-white p-5 shadow-sm">
                        <div className="text-sm text-gray-600">Gastado</div>
                        <div className="mt-1 text-2xl font-bold text-gray-900">{loadingSummary ? "—" : formatPrice(summary?.total_spent ?? 0)}</div>
                    </div>
                </div>

                {/* Toolbar filtros (igual que antes) */}
                <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-wrap gap-2">
                        {[
                            { key: undefined as unknown as string, label: "Todos" },
                            { key: "pending", label: "Pendiente" },
                            { key: "processing", label: "Procesando" },
                            { key: "shipped", label: "Enviado" },
                            { key: "delivered", label: "Entregado" },
                            { key: "cancelled", label: "Cancelado" },
                            { key: "returned", label: "Devuelto" },
                        ].map((t) => {
                            const isActive = (status ?? undefined) === (t.key as any);
                            return (
                                <button key={String(t.key)} onClick={() => { setStatus(t.key as any); setPage(1); }} className="focus:outline-none">
                                    <Badge variant={isActive ? "success" : "outline"}>{t.label}</Badge>
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex w-full items-center gap-2 md:w-auto">
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">Ordenar</label>
                            <select
                                value={sort}
                                onChange={(e) => { setSort(e.target.value); setPage(1); }}
                                className="rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="-created_at">Más recientes</option>
                                <option value="created_at">Más antiguos</option>
                                <option value="-total_amount">Total: de mayor a menor</option>
                                <option value="total_amount">Total: de menor a mayor</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">Por página</label>
                            <select
                                value={perPage}
                                onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                className="rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                {[10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Tabla / tarjetas */}
                <div className="rounded-2xl border bg-white shadow-sm">
                    {loadingList ? (
                        <div className="grid place-items-center p-10 text-gray-500">Cargando pedidos…</div>
                    ) : error ? (
                        <div className="p-6 text-center text-red-600">{error}</div>
                    ) : orders.length === 0 ? (
                        <div className="p-6 text-center text-gray-600">No tienes pedidos con los filtros actuales.</div>
                    ) : (
                        <>
                            {/* Desktop */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Pedido</th>
                                            <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Fecha</th>
                                            <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Total</th>
                                            <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Estado Pedido</th>
                                            <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Pago</th>
                                            <th className="px-5 py-3"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white">
                                        {orders.map((o) => (
                                            <tr key={o.id}>
                                                <td className="px-5 py-4">
                                                    <div className="flex flex-col">
                                                        <span className="font-medium text-gray-900">{o.order_number}</span>
                                                        <span className="text-xs text-gray-500">ID: {o.id}</span>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4 text-gray-700">{new Date((o as any).created_at).toLocaleString()}</td>
                                                <td className="px-5 py-4 text-right font-medium text-gray-900">{formatPrice(parseFloat((o as any).total_amount || 0), (o as any).currency || 'PEN')}</td>
                                                <td className="px-5 py-4"><Badge variant={orderVariant((o as any).status)}>{tOrderStatus((o as any).status)}</Badge></td>
                                                <td className="px-5 py-4">
                                                    <Badge variant={paymentVariant((o as any).payment_status)}>{tPaymentStatus((o as any).payment_status)}</Badge>
                                                    {typeof (o as any).refund_request_status !== "undefined" && (o as any).refund_request_status && (
                                                        <div className="mt-1">
                                                            <Badge variant={refundVariant((o as any).refund_request_status)}>
                                                                {tRefundStatus((o as any).refund_request_status)}
                                                            </Badge>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-5 py-4 text-right flex items-center justify-end gap-2">
                                                    <Button onClick={() => navigate(`/order-confirmation/${o.id}`)} className="px-3 py-1 text-sm">Ver</Button>

                                                    <Button
                                                        variant="outline"
                                                        className="px-3 py-1 text-sm border-red-200 text-red-600 hover:bg-red-50"
                                                        disabled={!canCancel(o)}
                                                        onClick={() => requestCancel(o.id)}
                                                    >
                                                        Cancelar
                                                    </Button>

                                                    <Button
                                                        variant="outline"
                                                        className="px-3 py-1 text-sm border-amber-200 text-amber-700 hover:bg-amber-50"
                                                        //disabled={!isDelivered(o) || (o as any).refund_request_status === "pending"}
                                                        disabled={!isDelivered(o) || (o as any).refund_request_status}
                                                        onClick={() => requestRefund(o.id)}
                                                    >
                                                        Reembolso
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile */}
                            <div className="md:hidden">
                                <ul className="divide-y divide-gray-100">
                                    {orders.map((o) => (
                                        <li key={o.id} className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <div className="text-sm text-gray-500">Pedido</div>
                                                    <div className="font-semibold text-gray-900">{o.order_number}</div>
                                                </div>
                                                <Badge variant={orderVariant((o as any).status)}>{tOrderStatus((o as any).status)}</Badge>
                                            </div>
                                            <div className="mt-2 text-sm text-gray-600">{new Date((o as any).created_at).toLocaleString()}</div>
                                            <div className="mt-1 text-base font-semibold text-gray-900">{formatPrice(parseFloat((o as any).total_amount || 0), (o as any).currency || 'PEN')}</div>
                                            <div className="mt-2 flex items-center justify-between">
                                                <Badge variant={paymentVariant((o as any).payment_status)}>{tPaymentStatus((o as any).payment_status)}</Badge>
                                                {typeof (o as any).refund_request_status !== "undefined" && (o as any).refund_request_status && (
                                                    <div className="mt-1">
                                                        <Badge variant={refundVariant((o as any).refund_request_status)}>
                                                            {tRefundStatus((o as any).refund_request_status)}
                                                        </Badge>
                                                    </div>
                                                )}
                                                <div className="flex gap-2">
                                                    <Button onClick={() => navigate(`/order-confirmation/${o.id}`)} className="px-3 py-1 text-sm">Ver</Button>
                                                    <Button
                                                        variant="outline"
                                                        className="px-3 py-1 text-sm border-red-200 text-red-600 hover:bg-red-50"
                                                        disabled={!canCancel(o)}
                                                        onClick={() => requestCancel(o.id)}
                                                    >
                                                        Cancelar
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        className="px-3 py-1 text-sm border-amber-200 text-amber-700 hover:bg-amber-50"
                                                        //disabled={!isDelivered(o) || (o as any).refund_request_status === "pending"}
                                                        disabled={!isDelivered(o) || (o as any).refund_request_status}
                                                        onClick={() => requestRefund(o.id)}
                                                    >
                                                        Reembolso
                                                    </Button>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </>
                    )}
                </div>

                {/* Paginación */}
                <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
                    <div className="text-sm text-gray-600">Mostrando {showingFrom}–{showingTo} de {meta.total}</div>
                    <div className="flex items-center gap-2">
                        <Button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-2 text-sm">Anterior</Button>
                        <span className="text-sm text-gray-600">Página {meta.current_page} de {meta.last_page}</span>
                        <Button disabled={page >= meta.last_page} onClick={() => setPage((p) => Math.min(meta.last_page || 1, p + 1))} className="px-3 py-2 text-sm">Siguiente</Button>
                    </div>
                </div>
            </div>

            {/* Dialog cancelar */}
            <ConfirmDialog
                open={cancelDialog.open}
                title="Cancelar pedido"
                description="Si confirmas, intentaremos cancelar el pedido. Si ya fue enviado/entregado no se podrá cancelar."
                confirmText="Cancelar pedido"
                cancelText="Volver"
                loading={cancelDialog.busy}
                onConfirm={confirmCancel}
                onClose={() => setCancelDialog({ open: false, orderId: null, reason: "", busy: false })}
            >
                <label className="text-sm text-gray-700">Motivo (opcional)</label>
                <textarea
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    rows={3}
                    value={cancelDialog.reason}
                    onChange={(e) => setCancelDialog((s) => ({ ...s, reason: e.target.value }))}
                    placeholder="Ej. me equivoqué en la dirección"
                />
            </ConfirmDialog>

            {/* Dialog reembolso */}
            <RefundRequestDialog state={refundDialog} setState={setRefundDialog} setOrders={setOrders} />
        </div>
    );
};

export default OrdersPage;

